/**
 * Store a string or number value in localStorage
 * @param key Storage key
 * @param value String or number to store
 * @returns true if successful, false if storage failed
 */
export const setLocalItem = (key: string, value: string | number): boolean => {
    try {
        window.localStorage.setItem(key, value.toString())
        return true
    } catch (e) {
        console.error(`Failed to set localStorage item "${key}":`, e)
        return false
    }
}

/**
 * Store a JSON object in localStorage
 * @param key Storage key
 * @param json Object to store
 * @returns true if successful, false if storage or serialization failed
 */
export const setLocalJSON = (key: string, json: object): boolean => {
    try {
        const jsonValue = JSON.stringify(json)
        window.localStorage.setItem(key, jsonValue)
        return true
    } catch (e) {
        console.error(`Failed to set localStorage JSON "${key}":`, e)
        return false
    }
}

/**
 * Get a string value from localStorage
 * @param key Storage key
 * @returns String value or null if not found or error
 */
export const getLocalItem = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key)
    } catch (e) {
        console.error(`Failed to get localStorage item "${key}":`, e)
        return null
    }
}

/**
 * Get and parse a JSON object from localStorage, with type safety and fallback
 * @param key Storage key
 * @param fallback Optional fallback value if not found or invalid
 * @returns Parsed value of type T, fallback, or null
 */
export function getLocalJSON<T = object>(key: string, fallback?: T): T | null {
    try {
        const value = window.localStorage.getItem(key)
        if (!value) return fallback ?? null
        const parsed = JSON.parse(value)
        return parsed as T
    } catch (e) {
        console.error(`Failed to get localStorage JSON "${key}":`, e)
        return fallback ?? null
    }
}

/**
 * Remove an item from localStorage
 * @param key Storage key
 * @returns true if successful, false if error
 */
export const removeLocalItem = (key: string): boolean => {
    try {
        window.localStorage.removeItem(key)
        return true
    } catch (e) {
        console.error(`Failed to remove localStorage item "${key}":`, e)
        return false
    }
}

/**
 * Clear all localStorage data
 * @returns true if successful, false if error
 */
export const clearLocal = (): boolean => {
    try {
        window.localStorage.clear()
        return true
    } catch (e) {
        console.error('Failed to clear localStorage:', e)
        return false
    }
}

/**
 * Check if localStorage is available and accessible
 * @returns true if localStorage is available, false otherwise
 */
export const isLocalStorageAvailable = (): boolean => {
    try {
        const test = '__localStorage_test__'
        window.localStorage.setItem(test, test)
        window.localStorage.removeItem(test)
        return true
    } catch {
        return false
    }
}
